import { useEffect, useRef } from 'react';
import { SidebarIcon } from './SidebarIcon';
import { Header } from '../Header';
import { ProcedureIcon, getProcedureIconAppearance } from './ProcedureIcon';
import './PatientBookingConfirmation.css';
import { AddToCalendarButton } from './AddToCalendarButton';
import { type CalendarAppointment } from '../appointmentCalendar';

interface PatientBookingConfirmationProps {
  procedureId: string;
  procedureType?: string;
  procedureName: string;
  practitionerName: string;
  date: string;
  time: string;
  onEditAppointment?: () => void;
  onOverview: () => void;
  heading?: string;
  description?: string;
  steps?: readonly string[];
  phase?: number;
  calendarAppointment?: CalendarAppointment;
}

const practicePhone = '052 624 46 25';

export function BookingOverviewButton({ onClick, className }: { onClick: () => void; className: string }) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    // Keyboard navigation may start outside the button. Safari also need not
    // focus a button on pointerdown, so clearing only on blur is insufficient.
    const keyboard = () => { ref.current?.removeAttribute('data-overview-focus'); };
    document.addEventListener('keydown', keyboard, true);
    return () => document.removeEventListener('keydown', keyboard, true);
  }, []);
  return <button ref={ref} type="button" onClick={onClick} className={className}
    onPointerDown={event => { event.currentTarget.dataset.overviewFocus = 'pointer'; }}
    onBlur={event => { delete event.currentTarget.dataset.overviewFocus; }}>Zu meinen Terminen</button>;
}

const Checkmark = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
    <path d="M5 10.5l3 3 7-7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SummaryIcon = ({ type }: { type: 'practitioner' | 'date' | 'time' }) => {
  if (type === 'time') {
    return <SidebarIcon name="clock" className="patient-booking-confirmation__summary-icon" />;
  }

  if (type === 'date') {
    return (
      <svg viewBox="0 0 24 24" className="patient-booking-confirmation__summary-icon" fill="currentColor" aria-hidden="true" focusable="false">
        {/* The calendar frame from InstrSurgeryIcon, without the day markers. */}
        <path fillRule="evenodd" clipRule="evenodd" d="M6 2h2v2h8V2h2v2h1a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V2ZM5 9v11h14V9H5Z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="patient-booking-confirmation__summary-icon" fill="currentColor" aria-hidden="true" focusable="false">
      <circle cx="12" cy="7" r="4" />
      <path d="M4 21v-2a8 5 0 0 1 16 0v2H4Z" />
    </svg>
  );
};

export function PatientBookingConfirmation({
  procedureId,
  procedureType,
  procedureName,
  practitionerName,
  date,
  time,
  onEditAppointment,
  onOverview,
  heading = 'Termin erfolgreich gebucht',
  description = 'Vielen Dank für Ihr Vertrauen. Wir haben Ihnen eine Bestätigungs-E-Mail mit allen Details zugesandt.',
  steps,
  phase,
  calendarAppointment,
}: PatientBookingConfirmationProps) {
  const details = [
    { type: 'procedure' as const, label: 'Behandlung', value: procedureName },
    { type: 'practitioner' as const, label: 'Behandler', value: practitionerName },
    { type: 'date' as const, label: 'Datum', value: date },
    { type: 'time' as const, label: 'Uhrzeit', value: `${time} Uhr` },
  ];

  return (
    <div className="patient-booking-confirmation">
      <Header compact steps={steps} phase={phase} />
      <div className="patient-booking-confirmation__content">
        <main className="patient-booking-confirmation__card" aria-labelledby="booking-confirmation-title">
          <div className="patient-booking-confirmation__heading">
            <div className="patient-booking-confirmation__success" aria-hidden="true">
              <span><Checkmark /></span>
            </div>
            <h1 id="booking-confirmation-title">{heading}</h1>
            <p className="patient-booking-confirmation__description">
              {description}
            </p>
          </div>

          <section className="patient-booking-confirmation__summary" aria-labelledby="booking-confirmation-summary">
            <h2 id="booking-confirmation-summary">Zusammenfassung</h2>
            <dl className="patient-booking-confirmation__details">
              {details.map(({ type, label, value }) => (
                <div key={type} className={`patient-booking-confirmation__detail patient-booking-confirmation__detail--${type}`}>
                  <dt>
                    <span
                      className="patient-booking-confirmation__icon-tile"
                      style={type === 'procedure' ? { color: getProcedureIconAppearance(procedureId, procedureType).color } : undefined}
                    >
                      {type === 'procedure'
                        ? <ProcedureIcon procedureId={procedureId} procedureName={procedureType} />
                        : <SummaryIcon type={type} />}
                    </span>
                    {label}
                  </dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </section>

          <div className={`patient-booking-confirmation__actions${onEditAppointment ? '' : ' patient-booking-confirmation__actions--booking'}`}>
            {onEditAppointment && <button type="button" onClick={onEditAppointment} className="patient-booking-confirmation__edit">Termin ändern</button>}
            <BookingOverviewButton onClick={onOverview} className="patient-booking-confirmation__overview" />
            {calendarAppointment && <AddToCalendarButton appointment={calendarAppointment} />}
          </div>

          <p className="patient-booking-confirmation__contact">
            Haben Sie Fragen? Kontaktieren Sie uns unter{' '}
            <a href={`tel:+41${practicePhone.replace(/\s/g, '').slice(1)}`}>{practicePhone}</a>.
          </p>
        </main>
      </div>
    </div>
  );
}
